import { FilterChip } from '@/mobile/shared/FilterChip';
import type { LancFilters } from './types';

interface LancFilterPanelProps {
  filters: LancFilters;
  onChange: (f: LancFilters) => void;
  cats: string[];
}

const eyebrow = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '0.10em',
  textTransform: 'uppercase' as const,
  fontWeight: 600,
  color: 'var(--fg-muted)',
  marginBottom: 8,
};

/** Painel dropdown de filtros (3 grupos: Tipo · Categoria · Período). */
export function LancFilterPanel({ filters, onChange, cats }: LancFilterPanelProps) {
  const activeCount =
    (filters.tipo !== 'todos' ? 1 : 0) +
    (filters.cat !== 'todas' ? 1 : 0) +
    (filters.periodo !== 'mes' ? 1 : 0);

  function set<K extends keyof LancFilters>(key: K, val: LancFilters[K]) {
    onChange({ ...filters, [key]: val });
  }

  function clear() {
    onChange({ tipo: 'todos', cat: 'todas', periodo: 'mes' });
  }

  return (
    <div
      role="dialog"
      aria-label="Painel de filtros"
      style={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        right: 0,
        width: 264,
        zIndex: 30,
        padding: 14,
        background: 'var(--card)',
        border: '1px solid var(--card-stroke)',
        borderRadius: 'var(--r-3)',
        boxShadow: 'var(--shadow-2)',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <strong style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)' }}>Filtros</strong>
        {activeCount > 0 && (
          <button
            onClick={clear}
            style={{
              fontSize: 11,
              color: 'var(--mint-deep)',
              fontWeight: 600,
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: '4px 0',
              minHeight: 44,
            }}
          >
            Limpar
          </button>
        )}
      </div>

      {/* Tipo */}
      <div>
        <div style={eyebrow}>Tipo</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <FilterChip active={filters.tipo === 'todos'} onClick={() => set('tipo', 'todos')}>
            Todos
          </FilterChip>
          <FilterChip active={filters.tipo === 'ok'} onClick={() => set('tipo', 'ok')}>
            Conciliado
          </FilterChip>
          <FilterChip active={filters.tipo === 'pending'} onClick={() => set('tipo', 'pending')}>
            Pendente
          </FilterChip>
        </div>
      </div>

      {/* Categoria */}
      <div>
        <div style={eyebrow}>Categoria</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <FilterChip active={filters.cat === 'todas'} onClick={() => set('cat', 'todas')}>
            Todas
          </FilterChip>
          {cats.map((c) => (
            <FilterChip key={c} active={filters.cat === c} onClick={() => set('cat', c)}>
              {c}
            </FilterChip>
          ))}
        </div>
      </div>

      {/* Período */}
      <div>
        <div style={eyebrow}>Período</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <FilterChip active={filters.periodo === 'mes'} onClick={() => set('periodo', 'mes')}>
            Este mês
          </FilterChip>
          <FilterChip active={filters.periodo === 'tri'} onClick={() => set('periodo', 'tri')}>
            3 meses
          </FilterChip>
          <FilterChip active={filters.periodo === 'ano'} onClick={() => set('periodo', 'ano')}>
            Este ano
          </FilterChip>
        </div>
      </div>
    </div>
  );
}
